import { connectNestConnection, createNestClient, type NestClient } from "../nest/client"
import { HARNESS_IDS, harnessIdForProvider } from "../modelRegistry"
import type { SessionEventSearchHit, SessionEventSearchRefresh } from "../nest/generated"
import { parseRepoRecord, parseTaskRecord, REPOS_NAMESPACE, TASKS_NAMESPACE } from "../persistence/records"
import type { HarnessId } from "../providerEvents/harnessEventTypes"
import type { ComputerBackend, ComputerSummary } from "./types"

const SEARCH_CONNECTION_TIMEOUT_MS = 6_000
const SEARCH_REQUEST_TIMEOUT_MS = 15_000
const SEARCH_CONCURRENCY = 3
export const SEARCH_RESULTS_PER_COMPUTER = 30
const RECORD_PAGE_LIMIT = 5_000
const MAX_RECORD_PAGES = 20

interface SearchTask {
    taskId: string
    workspaceId: string
    title: string
    projectName: string
    updatedAt: string
    sessionIds: string[]
    harnessIds: HarnessId[]
}

interface NodeSearchContext {
    client: NestClient
    tasks: Promise<SearchTask[]>
}

export interface TaskSearchMatch {
    taskId: string
    workspaceId: string
    title: string
    projectName: string
    updatedAt: string
    harnessIds: HarnessId[]
    kind: "title" | "message"
    messageMatchCount: number
    preview?: string
}

export interface ComputerTaskSearchResult {
    nodeId: string
    computerName: string
    matches: TaskSearchMatch[]
    refresh?: SessionEventSearchRefresh
    warning?: string
    error?: string
}

export interface FederatedTaskSearchResult {
    requested: number
    succeeded: number
    failed: number
    firstErrors: Array<{ nodeId: string; message: string }>
    computers: ComputerTaskSearchResult[]
}

export class FederatedTaskSearch {
    private readonly nodes = new Map<string, Promise<NodeSearchContext>>()

    constructor(
        private readonly backend: ComputerBackend,
        private readonly computers: ComputerSummary[]
    ) {}

    async search(query: string, signal?: AbortSignal): Promise<FederatedTaskSearchResult> {
        const normalized = query.trim()
        const result: FederatedTaskSearchResult = {
            requested: this.computers.length,
            succeeded: 0,
            failed: 0,
            firstErrors: [],
            computers: [],
        }
        let nextIndex = 0
        const nodeResults = Array.from<ComputerTaskSearchResult | undefined>({ length: this.computers.length })
        const worker = async () => {
            for (;;) {
                const index = nextIndex
                nextIndex += 1
                const computer = this.computers[index]
                if (!computer) return
                try {
                    nodeResults[index] = await this.searchComputer(computer, normalized, signal)
                    result.succeeded += 1
                } catch (error) {
                    const message = errorMessage(error)
                    nodeResults[index] = {
                        nodeId: computer.nodeId,
                        computerName: computer.name,
                        matches: [],
                        error: message,
                    }
                    result.failed += 1
                    if (result.firstErrors.length < 5) result.firstErrors.push({ nodeId: computer.nodeId, message })
                }
            }
        }
        await Promise.all(Array.from({ length: Math.min(SEARCH_CONCURRENCY, this.computers.length) }, () => worker()))
        result.computers = nodeResults.filter((node): node is ComputerTaskSearchResult => Boolean(node))
        return result
    }

    dispose(): void {
        for (const node of this.nodes.values()) {
            void node.then(({ client }) => client.close()).catch(() => undefined)
        }
        this.nodes.clear()
    }

    private async searchComputer(computer: ComputerSummary, query: string, signal?: AbortSignal): Promise<ComputerTaskSearchResult> {
        const node = await this.node(computer)
        const [tasks, eventResult] = await Promise.all([
            node.tasks,
            node.client
                .requestMethod("session.event.search", { query, limit: 100 }, { signal, timeoutMs: SEARCH_REQUEST_TIMEOUT_MS })
                .then((value) => ({ value }))
                .catch((error: unknown) => ({ error })),
        ])
        const bySession = new Map<string, SearchTask>()
        for (const task of tasks) {
            for (const sessionId of task.sessionIds) bySession.set(sessionId, task)
        }
        const titleMatches = tasks
            .map((task) => ({ task, score: titleScore(task.title, query) }))
            .filter((candidate) => candidate.score > 0)
            .sort((left, right) => right.score - left.score || right.task.updatedAt.localeCompare(left.task.updatedAt))
            .map(
                ({ task }): TaskSearchMatch => ({
                    taskId: task.taskId,
                    workspaceId: task.workspaceId,
                    title: task.title,
                    projectName: task.projectName,
                    updatedAt: task.updatedAt,
                    harnessIds: task.harnessIds,
                    kind: "title",
                    messageMatchCount: 0,
                })
            )

        let refresh: SessionEventSearchRefresh | undefined
        let warning: string | undefined
        let messageMatches: TaskSearchMatch[] = []
        if ("value" in eventResult) {
            refresh = eventResult.value.refresh
            messageMatches = mapEventMatches(eventResult.value.items, bySession)
        } else {
            warning = `Message search unavailable: ${errorMessage(eventResult.error)}`
        }
        const matches = retainHarnessFilterCandidates(mergeMatches(titleMatches, messageMatches))
        return {
            nodeId: computer.nodeId,
            computerName: computer.name,
            matches,
            ...(refresh ? { refresh } : {}),
            ...(warning ? { warning } : {}),
        }
    }

    private node(computer: ComputerSummary): Promise<NodeSearchContext> {
        const existing = this.nodes.get(computer.nodeId)
        if (existing) return existing
        const pending = (async () => {
            const client = createNestClient()
            try {
                const connection = await this.backend.getConnection(computer.nodeId)
                await connectNestConnection(client, connection, { timeoutMs: SEARCH_CONNECTION_TIMEOUT_MS })
                return { client, tasks: loadSearchTasks(client) }
            } catch (error) {
                client.close()
                throw error
            }
        })()
        this.nodes.set(computer.nodeId, pending)
        return pending
    }
}

async function loadSearchTasks(client: NestClient): Promise<SearchTask[]> {
    const [taskValues, repoValues] = await Promise.all([listNamespace(client, TASKS_NAMESPACE, "task."), listNamespace(client, REPOS_NAMESPACE, "repo.")])
    const projectNames = new Map<string, string>()
    for (const value of repoValues) {
        const repo = parseRepoRecord(value)
        if (repo) projectNames.set(repo.id, repo.name)
    }
    const tasks: SearchTask[] = []
    for (const value of taskValues) {
        const task = parseTaskRecord(value)
        if (!task || task.deletingAt) continue
        tasks.push({
            taskId: task.id,
            workspaceId: task.workspaceId,
            title: task.title,
            projectName: projectNames.get(task.repoId) ?? "Project",
            updatedAt: task.updatedAt,
            sessionIds: task.sessions.flatMap((session) => (session.nestSessionId ? [session.nestSessionId] : [])),
            harnessIds: Array.from(
                new Set(
                    task.sessions.flatMap((session) => {
                        const harnessId = harnessIdForProvider(session.providerId)
                        return harnessId ? [harnessId] : []
                    })
                )
            ),
        })
    }
    return tasks
}

async function listNamespace(client: NestClient, namespace: string, prefix: string): Promise<unknown[]> {
    const values: unknown[] = []
    let cursor: string | undefined
    for (let page = 0; page < MAX_RECORD_PAGES; page += 1) {
        const result = await client.requestMethod(
            "appData.list",
            { namespace, prefix, limit: RECORD_PAGE_LIMIT, ...(cursor ? { cursor } : {}) },
            { timeoutMs: SEARCH_REQUEST_TIMEOUT_MS }
        )
        values.push(...result.items.map((item) => item.value))
        if (!result.hasMore || !result.nextCursor) return values
        cursor = result.nextCursor
    }
    throw new Error(`Search metadata exceeded ${RECORD_PAGE_LIMIT * MAX_RECORD_PAGES} ${namespace} records`)
}

function mapEventMatches(hits: SessionEventSearchHit[], bySession: ReadonlyMap<string, SearchTask>): TaskSearchMatch[] {
    const matches = new Map<string, TaskSearchMatch>()
    for (const hit of hits) {
        const task = bySession.get(hit.sessionId)
        if (!task) continue
        const existing = matches.get(task.taskId)
        if (existing) {
            existing.messageMatchCount += 1
            continue
        }
        matches.set(task.taskId, {
            taskId: task.taskId,
            workspaceId: task.workspaceId,
            title: task.title,
            projectName: task.projectName,
            updatedAt: task.updatedAt,
            harnessIds: task.harnessIds,
            kind: "message",
            messageMatchCount: 1,
            preview: hit.preview,
        })
    }
    return [...matches.values()]
}

function mergeMatches(titleMatches: TaskSearchMatch[], messageMatches: TaskSearchMatch[]): TaskSearchMatch[] {
    const messageMatchesByTask = new Map(messageMatches.map((match) => [match.taskId, match]))
    const titleTaskIds = new Set(titleMatches.map((match) => match.taskId))
    const enrichedTitleMatches = titleMatches.map((match) => {
        const messageMatch = messageMatchesByTask.get(match.taskId)
        if (!messageMatch) return match
        return {
            ...match,
            preview: messageMatch.preview,
            messageMatchCount: messageMatch.messageMatchCount,
        }
    })
    return [...enrichedTitleMatches, ...messageMatches.filter((match) => !titleTaskIds.has(match.taskId))]
}

function retainHarnessFilterCandidates(matches: TaskSearchMatch[]): TaskSearchMatch[] {
    const retainedByHarness = new Map<HarnessId, number>(HARNESS_IDS.map((harnessId): [HarnessId, number] => [harnessId, 0]))
    const retained: TaskSearchMatch[] = []
    for (const [index, match] of matches.entries()) {
        let keep = index < SEARCH_RESULTS_PER_COMPUTER
        for (const harnessId of match.harnessIds) {
            const count = retainedByHarness.get(harnessId)
            if (count === undefined || count >= SEARCH_RESULTS_PER_COMPUTER) continue
            retainedByHarness.set(harnessId, count + 1)
            keep = true
        }
        if (keep) retained.push(match)
    }
    return retained
}

function titleScore(title: string, query: string): number {
    const normalizedTitle = title.toLocaleLowerCase()
    const normalizedQuery = query.toLocaleLowerCase()
    if (normalizedTitle === normalizedQuery) return 1_000
    if (normalizedTitle.startsWith(normalizedQuery)) return 800
    if (normalizedTitle.includes(normalizedQuery)) return 600
    const terms = normalizedQuery.split(/\s+/).filter(Boolean)
    return terms.length > 0 && terms.every((term) => normalizedTitle.includes(term)) ? 400 : 0
}

function errorMessage(error: unknown): string {
    return error instanceof Error ? error.message : String(error)
}
